// src/components/Message/FileOptionsMenu.tsx
import React, { useEffect, useRef } from 'react';
import { FileText, Download, X } from 'lucide-react';

interface FileOptionsMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onPreview: () => void;
  onDownload: () => void;
  position: { x: number; y: number };
}

const FileOptionsMenu: React.FC<FileOptionsMenuProps> = ({
  isOpen,
  onClose,
  onPreview,
  onDownload,
  position
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleScroll = () => {
      onClose(); // Close menu when scrolling
    };

    const handleWheel = () => {
      onClose(); // Close menu when using mouse wheel
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll, true); // Close on scroll
      document.addEventListener('wheel', handleWheel, true); // Close on mouse wheel
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('wheel', handleWheel, true);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Adjust position to ensure menu stays within viewport
  const adjustedPosition = {
    x: Math.min(position.x, window.innerWidth - 200),
    y: Math.min(position.y, window.innerHeight - 150)
  };

  return (
    <div 
      ref={menuRef}
      className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200"
      style={{
        top: `${adjustedPosition.y}px`,
        left: `${adjustedPosition.x}px`,
        minWidth: '180px'
      }}
    >
      <div className="flex justify-between items-center p-2 border-b border-gray-200">
        <span className="font-medium">File Options</span>
        <button 
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-full"
        >
          <X size={16} />
        </button>
      </div>
      <div className="p-1">
        <button
          onClick={() => {
            onPreview();
            onClose();
          }}
          className="w-full text-left px-3 py-2 hover:bg-violet-50 rounded-md flex items-center gap-2 text-sm"
        >
          <FileText size={16} />
          Preview
        </button>
        <button
          onClick={() => {
            onDownload();
            onClose();
          }}
          className="w-full text-left px-3 py-2 hover:bg-violet-50 rounded-md flex items-center gap-2 text-sm"
        >
          <Download size={16} />
          Download
        </button>
      </div>
    </div>
  );
};

export default FileOptionsMenu;